import { StandardEvents } from '@shopify/events';

/**
 * <kr-product-gallery> — thumbnail rail, variant sync and an optional lightbox.
 *
 * The slides themselves are a <kr-slider>, so swiping, dots and scroll-snap already work
 * without this element. It adds three things:
 *   1. Thumbnails that drive the slider, and follow it back when the user swipes. The
 *      rail is a single-row horizontal scroller (CSS), so this element keeps the active
 *      thumb in view, tracks which edge is overflowing for the fade, and translates
 *      mouse drags into scrolling — the rail has no arrows by design.
 *   2. Variant sync: Horizon's variant picker dispatches a productSelect event whose
 *      promise resolves with the selected variant, so the gallery can jump to that
 *      variant's media.
 *   3. A minimal native <dialog> lightbox. Deliberately not Horizon's <zoom-dialog>,
 *      whose markup contract (drag-zoom-wrapper, thumbnail rail, view transitions) is far
 *      more than this design needs.
 */
class KrProductGallery extends HTMLElement {
  #section = null;
  #adapts = false;
  #railObserver = null;
  #railAbort = null;
  #dragAbort = null;
  #dragging = false;
  #overflowFrame = 0;

  connectedCallback() {
    this.slider = this.querySelector('kr-slider');
    this.thumbs = Array.from(this.querySelectorAll('[data-kr-thumb]'));
    this.slides = Array.from(this.querySelectorAll('[data-kr-slide]'));
    this.#adapts = this.hasAttribute('data-adapt');

    this.thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => this.select(index));
    });

    this.slider?.addEventListener('kr-slider:change', (event) => this.#markActive(event.detail.index));

    this.#section = this.closest('.shopify-section');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect);

    this.#setupThumbRail();
    this.#setupLightbox();
    this.#markActive(0);
  }

  disconnectedCallback() {
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
    this.#railObserver?.disconnect();
    this.#railAbort?.abort();
    this.#dragAbort?.abort();
    cancelAnimationFrame(this.#overflowFrame);
  }

  /** @param {number} index */
  select(index) {
    this.slider?.goTo(index);
    this.#markActive(index);
  }

  #markActive(index) {
    this.#syncRatio(index);

    this.thumbs.forEach((thumb, i) => {
      const isActive = i === index;
      thumb.classList.toggle('kr-gallery__thumb--active', isActive);
      thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    this.#revealThumb(index);
  }

  /**
   * Adapt mode: the stage follows whichever slide is active, so a vertical shot gets a
   * vertical frame and a square one a square frame — no letterboxing, no crop.
   *
   * One shared ratio rather than one per slide, because every slide sits in the same
   * implicit grid row of `.kr-slider__viewport` and so shares its height regardless;
   * a per-slide ratio would only move the dead space, not remove it.
   */
  #syncRatio(index) {
    if (!this.#adapts) return;

    const ratio = this.slides[index]?.dataset.ratio;
    if (ratio) this.style.setProperty('--kr-gallery-ratio', ratio);
  }

  #onProductSelect = (event) => {
    event.promise
      ?.then(({ detail, variant }) => {
        // `resource` is the variant JSON the picker read back out of the refetched page.
        const mediaId = detail?.resource?.featured_media?.id ?? variant?.featuredMedia?.id;
        if (!mediaId) return;

        const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
        if (index >= 0) this.select(index);
      })
      .catch(() => {
        /* Unavailable variant combinations resolve without media — nothing to sync. */
      });
  };

  /* --- Thumbnail rail ------------------------------------------------------ */

  #setupThumbRail() {
    this.thumbRail = this.querySelector('[data-kr-thumbs]');
    if (!this.thumbRail) return;

    const controller = new AbortController();
    this.#railAbort = controller;
    const { signal } = controller;

    this.thumbRail.addEventListener('scroll', this.#queueOverflowUpdate, { passive: true, signal });
    this.thumbRail.addEventListener('pointerdown', this.#onThumbPointerDown, { signal });

    // A ResizeObserver rather than a window listener: it also fires once on observe
    // (so the first measurement happens after layout, not from connectedCallback where
    // the thumbs may still be unsized) and catches thumbnail_size edits in the editor.
    this.#railObserver = new ResizeObserver(this.#queueOverflowUpdate);
    this.#railObserver.observe(this.thumbRail);
  }

  #queueOverflowUpdate = () => {
    cancelAnimationFrame(this.#overflowFrame);
    this.#overflowFrame = requestAnimationFrame(this.#updateThumbOverflow);
  };

  /** Records which edge has more thumbs behind it, so the CSS can fade just that edge. */
  #updateThumbOverflow = () => {
    const rail = this.thumbRail;
    if (!rail) return;

    const max = rail.scrollWidth - rail.clientWidth;
    // RTL scrolls into negative scrollLeft, so distance travelled from the rail's
    // start is the absolute value in both directions.
    const travelled = Math.abs(rail.scrollLeft);

    let state = 'none';
    if (max > 1) {
      const atStart = travelled <= 1;
      const atEnd = travelled >= max - 1;
      if (atStart && atEnd) state = 'none';
      else if (atStart) state = 'end';
      else if (atEnd) state = 'start';
      else state = 'both';
    }

    rail.dataset.overflow = state;
  };

  /** Scrolls the rail the minimum amount needed to bring a thumb fully into view. */
  #revealThumb(index) {
    const rail = this.thumbRail;
    const thumb = this.thumbs[index];
    if (!rail || !thumb) return;

    // Rect deltas rather than offsetLeft maths: they are physical in both LTR and RTL,
    // and scrollBy clamps itself at the ends. scrollIntoView is deliberately avoided —
    // it scrolls ancestors too, which would jerk the page inside the sticky gallery.
    const railRect = rail.getBoundingClientRect();
    const thumbRect = thumb.getBoundingClientRect();
    const margin = 8; // Matches the rail gap, and keeps the thumb clear of the fade.

    let delta = 0;
    if (thumbRect.left < railRect.left + margin) {
      delta = thumbRect.left - railRect.left - margin;
    } else if (thumbRect.right > railRect.right - margin) {
      delta = thumbRect.right - railRect.right + margin;
    }
    if (!delta) return;

    rail.scrollBy({
      left: delta,
      behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }

  // The rail has no arrows by design, so a wheel-only mouse would have no way to reach
  // the thumbs past the edge. Touch and trackpads scroll natively and are left alone.
  #onThumbPointerDown = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0 || this.#dragging) return;

    event.preventDefault();

    const rail = this.thumbRail;
    const startX = event.clientX;
    const startScroll = rail.scrollLeft;
    let moved = false;

    const controller = new AbortController();
    const { signal } = controller;
    this.#dragAbort = controller;
    this.#dragging = true;

    // A drag ends with a click on whichever thumb is under the cursor; swallow it so
    // scrolling the rail never changes the selected media. Deliberately NOT tied to
    // `signal`: the click fires after pointerup, when the controller is already aborted.
    const suppressClick = (clickEvent) => {
      clickEvent.preventDefault();
      clickEvent.stopPropagation();
    };

    const onPointerMove = (e) => {
      const delta = startX - e.clientX;
      if (!moved) {
        if (Math.abs(delta) < 3) return;
        moved = true;
        rail.setPointerCapture(e.pointerId);
        rail.dataset.dragging = '';
        document.addEventListener('click', suppressClick, { capture: true, once: true });
      }
      rail.scrollLeft = startScroll + delta;
    };

    const onPointerUp = () => {
      controller.abort();
      this.#dragAbort = null;
      this.#dragging = false;
      delete rail.dataset.dragging;

      // The drag's own click (if any) dispatches synchronously before this timeout, so
      // this only cleans up sessions that ended without one (pointercancel, release
      // outside the window) — otherwise the next unrelated click would be swallowed.
      if (moved) setTimeout(() => document.removeEventListener('click', suppressClick, true), 0);
    };

    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerUp, { signal });
    document.addEventListener('pointercancel', onPointerUp, { signal });
  };

  /* --- Lightbox ----------------------------------------------------------- */

  #setupLightbox() {
    this.dialog = this.querySelector('[data-kr-lightbox]');
    if (!this.dialog) return;

    this.dialogImage = this.dialog.querySelector('[data-kr-lightbox-image]');
    this.sources = this.slides.map((slide) => {
      const img = slide.querySelector('img');
      return img ? { src: img.currentSrc || img.src, srcset: img.srcset, alt: img.alt } : null;
    });

    this.querySelectorAll('[data-kr-zoom]').forEach((trigger, index) => {
      trigger.addEventListener('click', () => this.openLightbox(index));
    });

    this.dialog.querySelector('[data-kr-lightbox-close]')?.addEventListener('click', () => this.dialog.close());
    // Clicking the backdrop closes; the image itself sits in an inner element.
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });
  }

  /** @param {number} index */
  openLightbox(index) {
    const source = this.sources?.[index];
    if (!source || !this.dialogImage) return;

    this.dialogImage.src = source.src;
    this.dialogImage.srcset = source.srcset;
    this.dialogImage.alt = source.alt;
    this.dialog.showModal();
  }
}

/**
 * <kr-sticky-atc> — reveals a slim bar once the real Add to cart button scrolls away.
 *
 * The bar's button still forwards its click to the real one, so there is exactly one
 * add-to-cart code path. On top of that:
 *   - its option dropdowns drive the page's variant picker, so the gallery, the real
 *     button and this bar all stay on the same variant;
 *   - a variant change anywhere on the page updates this bar's price, savings and image;
 *   - right before forwarding the click, the real form's variant id is set to the bar's
 *     variant, so a quick select-then-click never adds the previous variant.
 */
class KrStickyAtc extends HTMLElement {
  #observer = null;
  #section = null;
  #target = null;
  #product = null;
  #moneyFormat = '${{amount}}';

  connectedCallback() {
    const targetSelector = this.dataset.target;
    const target = targetSelector ? document.querySelector(targetSelector) : null;
    if (!target) return;
    this.#target = target;

    try {
      this.#product = JSON.parse(this.querySelector('[data-kr-sticky-product]')?.textContent || 'null');
      this.#moneyFormat =
        JSON.parse(this.querySelector('[data-kr-sticky-money]')?.textContent || 'null') || this.#moneyFormat;
    } catch {
      this.#product = null;
    }

    this.proxy = this.querySelector('[data-kr-sticky-submit]');
    this.image = this.querySelector('[data-kr-sticky-image]');
    this.price = this.querySelector('[data-kr-sticky-price]');
    this.compare = this.querySelector('[data-kr-sticky-compare]');
    this.badge = this.querySelector('[data-kr-sticky-badge]');
    this.selects = Array.from(this.querySelectorAll('[data-kr-sticky-option]'));

    this.proxy?.addEventListener('click', this.#onSubmit);
    this.selects.forEach((select) => select.addEventListener('change', this.#onOptionChange));

    this.#section = this.closest('.shopify-section');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect);

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        this.dataset.visible = entry.isIntersecting || entry.boundingClientRect.top > 0 ? 'false' : 'true';
      },
      { rootMargin: '0px 0px -100% 0px' }
    );
    this.#observer.observe(target);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
  }

  /* --- Variant state ------------------------------------------------------ */

  /** The variant matching the bar's dropdowns (or its own variant when there are none). */
  #currentVariant() {
    const variants = this.#product?.variants ?? [];
    if (!this.selects.length) {
      return variants.find((v) => String(v.id) === this.dataset.variantId) ?? variants[0] ?? null;
    }
    const values = this.selects.map((select) => select.value);
    return variants.find((v) => v.options.every((value, i) => value === values[i])) ?? null;
  }

  #onOptionChange = (event) => {
    const variant = this.#currentVariant();
    this.#render(variant);

    const select = /** @type {HTMLSelectElement} */ (event.currentTarget);
    this.#syncMainPicker(Number(select.dataset.krStickyOption), select.value);
  };

  /** Selects the same option value in the page's own variant picker. */
  #syncMainPicker(index, value) {
    const picker = this.#section?.querySelector('variant-picker');
    if (!picker) return;

    const groups = picker.querySelectorAll('.variant-option');
    const group = groups[index] ?? picker;
    const optionName = this.#product?.options?.[index];

    const radio = Array.from(group.querySelectorAll('input[type="radio"]')).find(
      (input) => input.value === value && (groups[index] || !optionName || input.name.startsWith(optionName))
    );
    if (radio) {
      if (!radio.checked) radio.click();
      return;
    }

    const select = group.querySelector('select');
    if (!select) return;
    const match = Array.from(select.options).find(
      (option) => option.value === value || option.textContent.trim() === value || option.dataset.value === value
    );
    if (match && !match.selected) {
      select.value = match.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /** A variant was chosen elsewhere on the page — mirror it in the bar. */
  #onProductSelect = (event) => {
    if (event.target instanceof Element && event.target.closest('product-card')) return;

    event.promise
      ?.then(({ detail, variant }) => {
        if (!this.#product) return;
        if (detail?.productId && String(detail.productId) !== String(this.#product.id)) return;

        const rawId = detail?.resource?.id ?? variant?.id;
        if (!rawId) return;
        const id = String(rawId).split('/').pop();

        const match = this.#product.variants.find((v) => String(v.id) === id);
        if (!match) return;

        this.selects.forEach((select, i) => {
          if (match.options[i] != null) select.value = match.options[i];
        });
        this.#render(match);
      })
      .catch(() => {
        /* Unavailable combinations resolve without a variant — the bar keeps its own state. */
      });
  };

  /* --- Rendering ---------------------------------------------------------- */

  #render(variant) {
    if (variant) {
      this.dataset.variantId = String(variant.id);

      if (this.price) this.price.innerHTML = this.#formatMoney(variant.price);

      const hasCompare = variant.compare_at_price > variant.price;
      if (this.compare) {
        this.compare.hidden = !hasCompare;
        if (hasCompare) this.compare.innerHTML = this.#formatMoney(variant.compare_at_price);
      }
      if (this.badge) {
        this.badge.hidden = !hasCompare;
        if (hasCompare) {
          const percent = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
          this.badge.textContent = `${this.dataset.saveLabel || 'Save'} ${percent}%`;
        }
      }

      const src = variant.featured_image?.src ?? this.#product?.featured_image;
      if (this.image && src) {
        this.image.src = `${src}${src.includes('?') ? '&' : '?'}width=160`;
      }
    }

    if (!this.proxy) return;
    let label = this.dataset.labelAdd;
    let disabled = false;
    if (!variant) {
      label = this.dataset.labelUnavailable;
      disabled = true;
    } else if (!variant.available) {
      label = this.dataset.labelSoldOut;
      disabled = true;
    }
    if (label) this.proxy.textContent = label;
    this.proxy.disabled = disabled;
  }

  #formatMoney(cents) {
    const format = typeof this.#moneyFormat === 'string' && this.#moneyFormat ? this.#moneyFormat : '${{amount}}';
    const amount = Number(cents) / 100;
    const group = (value, decimals, thousands, decimal) => {
      const [whole, fraction] = value.toFixed(decimals).split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
    };

    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return group(amount, 0, ',', '.');
        case 'amount_with_comma_separator':
          return group(amount, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return group(amount, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return group(amount, 2, "'", '.');
        default:
          return group(amount, 2, ',', '.');
      }
    });
  }

  /* --- Add to cart -------------------------------------------------------- */

  #onSubmit = () => {
    const variant = this.#currentVariant();
    if (this.#product && (!variant || !variant.available)) return;

    const form = this.#target.querySelector('form');
    const idInput = form?.querySelector('input[name="id"]');
    if (variant && idInput) {
      idInput.value = String(variant.id);
      idInput.disabled = false;
    }

    const button = this.#target.querySelector('button[type="submit"], button');
    if (button && !button.disabled) {
      button.click();
    } else {
      form?.requestSubmit();
    }
  };
}

if (!customElements.get('kr-product-gallery')) {
  customElements.define('kr-product-gallery', KrProductGallery);
}

if (!customElements.get('kr-sticky-atc')) {
  customElements.define('kr-sticky-atc', KrStickyAtc);
}
